import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { generateReportPDF } from "@/lib/pdf";
import { Download, ArrowLeft, Check, X as XIcon } from "lucide-react";

export default function ReportDetail() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    api.get(`/reports/${id}`).then(async ({ data }) => {
      setReport(data);
      const { data: tpl } = await api.get(`/checklists/${data.type}`);
      setTemplate(tpl);
    });
  }, [id]);

  if (!report) return <div className="text-[color:var(--text-secondary)]">Loading report…</div>;

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";
  const items = template?.items || [];

  return (
    <div className="space-y-6" data-testid="report-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/reports" className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back to reports</Link>
        <button className="hos-btn-primary flex items-center gap-2" onClick={() => generateReportPDF(report, template)} data-testid="download-pdf-btn">
          <Download size={16}/> Download PDF
        </button>
      </div>

      <div className="hos-card p-6">
        <div className="label-caps">House of Spells — Times Square</div>
        <h1 className="font-heading text-3xl mt-1">{report.type === "opening" ? "Store Opening Report" : "Store Closing Report"}</h1>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 text-sm">
          <Meta k="Report #" v={report.report_number}/>
          <Meta k="Business Date" v={report.business_date}/>
          <Meta k="Manager" v={report.manager_name}/>
          <Meta k="Employee ID" v={report.manager_employee_id || "—"}/>
          <Meta k="Start" v={fmt(report.start_time)}/>
          <Meta k="Completed" v={fmt(report.completed_time)}/>
          <Meta k={`Actual ${report.type === "opening" ? "Open" : "Close"}`} v={fmt(report.actual_time)}/>
          <Meta k="Completion" v={`${report.completion_pct}%`}/>
        </div>
      </div>

      <div className="hos-card p-6">
        <h2 className="font-heading text-2xl mb-3">Checklist</h2>
        <ul className="divide-y divide-[color:var(--border)]">
          {items.map((it, i) => {
            const r = (report.responses || []).find((x) => x.item_id === it.id) || {};
            return (
              <li key={it.id} className="py-3 flex items-start gap-3">
                <div className="mt-1">
                  {r.completed ? <Check size={18} className="text-[color:var(--success)]"/>
                    : r.unable_reason ? <XIcon size={18} className="text-[color:var(--warning)]"/>
                    : <span className="inline-block w-4 h-4 rounded border border-[color:var(--border)]" />}
                </div>
                <div className="flex-1">
                  <div>{i+1}. {it.title} {it.section && <span className="text-xs text-[color:var(--text-secondary)]">· {it.section}</span>}</div>
                  <div className="text-xs text-[color:var(--text-secondary)] mt-0.5">
                    {r.completed && `Completed ${fmt(r.completed_at)} by ${r.completed_by_name}`}
                    {r.unable_reason && `Unable: ${r.unable_reason}`}
                    {r.notes && ` · ${r.notes}`}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {report.pos_reconciliation && (
        <div className="hos-card p-6">
          <h2 className="font-heading text-2xl mb-3">POS Reconciliation</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <Meta k="Tills" v={report.pos_reconciliation.tills_closed}/>
            <Meta k="Expected" v={`$${(report.pos_reconciliation.expected_cash || 0).toFixed(2)}`}/>
            <Meta k="Actual" v={`$${(report.pos_reconciliation.actual_cash || 0).toFixed(2)}`}/>
            <Meta k="Difference" v={`$${(report.pos_reconciliation.difference || 0).toFixed(2)}`}/>
            <Meta k="Verified by" v={report.pos_reconciliation.verified_by || "—"}/>
            <div className="col-span-2 sm:col-span-3"><Meta k="Explanation" v={report.pos_reconciliation.explanation || "—"}/></div>
          </div>
        </div>
      )}

      <div className="hos-card p-6">
        <h2 className="font-heading text-2xl mb-3">Manager Declaration</h2>
        <div className="text-sm text-[color:var(--text-secondary)] italic mb-4">
          "{report.type === "opening"
            ? "I confirm that I have completed the store-opening inspection and that the store is safe, operational, clean, and ready for customers, except for any issues documented in this report."
            : "I confirm that I have completed the store-closing inspection, secured all cash and required areas, shut down the listed systems, armed the security alarm, and confirmed that the store is fully secured, except for any issues documented in this report."}"
        </div>
        {report.signature && <img src={report.signature} alt="signature" className="h-24 border border-[color:var(--border)] rounded-md bg-white p-2"/>}
        <div className="text-sm mt-2">{report.signature_name} · {report.signature_employee_id}</div>
        <div className="text-xs text-[color:var(--text-secondary)]">Signed {fmt(report.signature_timestamp || report.completed_time)}</div>
      </div>

      {report.edits && report.edits.length > 0 && (
        <div className="hos-card p-6">
          <h2 className="font-heading text-2xl mb-3">Audit History</h2>
          <ul className="space-y-2 text-sm">
            {report.edits.map((e, i) => (
              <li key={`${e.at}-${e.by_id || i}`} className="border-l-2 border-[color:var(--warning)] pl-3">
                <div>{fmt(e.at)} — {e.by_name}</div>
                <div className="text-[color:var(--text-secondary)]">Reason: {e.reason}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Meta({ k, v }) {
  return (
    <div>
      <div className="label-caps">{k}</div>
      <div className="text-[color:var(--text-primary)]">{String(v ?? "—")}</div>
    </div>
  );
}
